/**
 * @file Renders the add, edit, refresh or connect button of an editable section.
 */
import React, { useState } from "react";
import { Button } from "antd";
import type { ButtonProps } from "antd";
import { useNavigate } from "react-router-dom";
import { PlusOutlined, EditOutlined, RedoOutlined } from "@ant-design/icons";

const LABEL_ADD = "Add";
const LABEL_EDIT = "Edit";
const LABEL_REDO = "Refresh";
const LABEL_CONNECT = "Connect";

export const TYPES = {
  ADD: "add",
  EDIT: "edit",
  REDO: "redo",
  CONNECT: "connect",
} as const;

export type EditButtonType = typeof TYPES[keyof typeof TYPES];

export type EditButtonProps = {
  type?: EditButtonType;
  path?: string;
  onClick?: () => void | Promise<void>;
} & Omit<ButtonProps, "onClick" | "type">;

const LABELS_MAP: Record<EditButtonType, string> = {
  [TYPES.ADD]: LABEL_ADD,
  [TYPES.EDIT]: LABEL_EDIT,
  [TYPES.REDO]: LABEL_REDO,
  [TYPES.CONNECT]: LABEL_CONNECT,
};

const ICONS_MAP: Record<EditButtonType, JSX.Element> = {
  [TYPES.ADD]: <PlusOutlined />,
  [TYPES.EDIT]: <EditOutlined />,
  [TYPES.REDO]: <RedoOutlined />,
  [TYPES.CONNECT]: <PlusOutlined />,
};

/**
 * Renders a labeled, icon button for its type, that navigates to a path or runs a click
 * handler, and shows a loading state while an async handler runs.
 */
const EditButton = ({
  type = TYPES.EDIT,
  path = "",
  onClick,
  ...otherButtonProps
}: EditButtonProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleClick = onClick ?? (() => navigate(path));

  const icon = ICONS_MAP[type];
  const title = LABELS_MAP[type];

  return (
    <Button
      icon={icon}
      size="small"
      shape="round"
      style={{ float: "right" }}
      loading={isLoading}
      onClick={async () => {
        setIsLoading(true);
        await handleClick();
        setIsLoading(false);
      }}
      {...otherButtonProps}
    >
      {title}
    </Button>
  );
};

export default EditButton;
